import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  StagedAfeChange,
  StagedScheduleChange,
  StagedSettingsChange,
  StagedVolumeChange,
} from 'components/agent-chat/staged-changes';
import {
  diffPendingSettings,
  type PendingSettingValues,
} from 'components/agent-chat/pending-settings';
import { MASTER_DATA_QUERY_KEY } from './hooks';
import {
  commitOverrideToWorkingConfig,
  type OverrideCommitSinks,
} from './override-commit';
import { paintNewestOperation, seedPriceDeckId } from './utils';
import type {
  CalcDetail,
  MasterDataResponse,
  WorkingConfig,
  OverrideFields,
  SlotOverrideOperation,
  SlotRow,
} from './types';

/**
 * Turns a staged change from the agent into the slot-override operation this page
 * appends. `readStagedChange` parses the tool payload — shared with the eval-3
 * workspace — and this is the half that is specific to eval3-new: the workspace
 * writes working atoms, and here every per-slot change becomes one `ids` operation
 * on the same channel the Bulk Edit panel writes to.
 *
 * Split out of the handler in `eval3-new-content.tsx` so the mapping can be checked
 * without mounting the page root. Settings are not handled here: they are run-level
 * draft fields rather than slot overrides, and go straight to the working config.
 *
 * @param {object} change - The parsed staged change, minus the settings kind.
 * @return {object} The operation to append.
 */
export function agentChangeToOperation(
  change: StagedAfeChange | StagedVolumeChange | StagedScheduleChange
): Extract<SlotOverrideOperation, { overrides: OverrideFields }> {
  // Schedule dates ride the same slot-override channel; the calculator moves the
  // derived AFE cost dates itself, so nothing extra is written here.
  if (change.kind === 'schedule') {
    return {
      kind: 'ids',
      slotIds: change.slotIds,
      overrides: { ...change.dates },
    };
  }

  if (change.kind === 'volume') {
    return {
      kind: 'ids',
      slotIds: change.slotIds,
      overrides: {
        volumeSourceId: change.volumeSourceId,
        ...(change.volumeSourceType
          ? { volumeSourceType: change.volumeSourceType }
          : {}),
        // Carries the grid's link for the new curve; without it the row keeps its
        // base href and links to the curve it just moved off.
        ...(change.volumeSourceHref
          ? { volumeSourceHref: change.volumeSourceHref }
          : {}),
        ...(change.forecastSource
          ? { forecastSource: change.forecastSource }
          : {}),
        ...(change.dcaScenario ? { dcaScenario: change.dcaScenario } : {}),
      },
    };
  }

  return {
    kind: 'ids',
    slotIds: change.slotIds,
    overrides: { afeSourceId: change.afeSourceId, ...change.costDates },
    // The AFE well name and its link are BE-derived columns the FE cannot recompute, so
    // they ride along to keep the table aligned until the next read.
    //
    // Sent even when null, which is the correction: this used to be gated on a truthy
    // well name, so an AFE with no well name of its own left the PREVIOUS AFE's name
    // sitting beside the new afeSourceId — the outcome eval-3's own comment calls wrong,
    // and the two apply paths disagreed on it. `afeSourceId` changes in this same
    // operation, so absent means clear, and the paint treats null as clear.
    displayPatch: {
      afeWellName: change.afeWellName ?? null,
      afeSourceHref: change.afeSourceHref ?? null,
    },
  };
}

/**
 * Commits one agent-staged operation to the page: paint, then the shared commit (invalidate,
 * append, prune the schedule dialog's channel).
 *
 * ── THE SAME EDIT, WHICHEVER HAND MAKES IT ───────────────────────────────────────────────
 *
 * "commitAgentOverride is unchanged and still invalidates neither allocations nor
 * slot-valuation-inputs."
 *
 * It did neither. It painted the cached slots pages, appended to the working config, and
 * stopped — so an edit the assistant staged left the allocations view and every open
 * tearsheet showing pre-override values, while the identical edit typed into the grid
 * refreshed all three. The BE-derived cascades a paint cannot compute (the five AFE cost
 * columns, which the calculator recomputes from the chosen AFE) never reconciled either,
 * because nothing was marked stale for the next navigation read to correct.
 *
 * `commitOverrideToWorkingConfig` is now the one copy of everything after the paint, and
 * `commitSlotOverride` calls it too, so the two paths cannot drift apart again — which is how
 * they came to differ in the first place.
 *
 * ── WHY IT IS A HOOK IN THIS FILE ────────────────────────────────────────────────────────
 *
 * The same reason `agentChangeToOperation` is here: the page root cannot be mounted in a
 * test, so anything left inline in it is unassertable. Lifted whole rather than rewritten —
 * the paint below is the code that was in `eval3-new-content.tsx`, unchanged.
 *
 * ⚠️ `setQueriesData` WITH A PARTIAL KEY, NOT `setQueryData`. The agent is not looking at a
 * particular page of the grid, so every cached slots page is painted rather than one exact
 * key. That is the one thing this path deliberately does differently from a grid edit.
 *
 * @param {number} packageId - The package whose caches are keyed.
 * @param {OverrideCommitSinks} sinks - The working config's append and schedule channel.
 * @return {Function} The commit callback.
 */
export function useCommitAgentOverride(
  packageId: number,
  sinks: OverrideCommitSinks
): (operation: SlotOverrideOperation) => void {
  const queryClient = useQueryClient();

  return useCallback(
    (operation: SlotOverrideOperation) => {
      const paint = (prev?: MasterDataResponse<SlotRow>) => {
        if (!prev) return prev;
        return { ...prev, rows: paintNewestOperation(prev.rows, operation) };
      };

      queryClient.setQueriesData<MasterDataResponse<SlotRow>>(
        { queryKey: [MASTER_DATA_QUERY_KEY, packageId, 'slots'] },
        paint
      );
      commitOverrideToWorkingConfig(queryClient, packageId, operation, sinks);
    },
    [sinks, packageId, queryClient]
  );
}

/**
 * A case's run takes its Type Curve and DCA from the case (13.1), and Calculate sends neither. Whether `evaluationCaseId` makes this page's run one.
 *
 * @param {number | null} evaluationCaseId - The case the run would use.
 * @return {boolean} Whether the run is case-level.
 */
const caseLevelRun = (evaluationCaseId: number | null | undefined): boolean =>
  evaluationCaseId !== null && evaluationCaseId !== undefined;

/**
 * The run-level settings this page holds that differ from the loaded run, for the agent's reads
 * (`diffPendingSettings`, shared with the other eval page). A case-level run leaves DCA out: the
 * page has no DCA pick then, and the run ignores one, so reporting it would tell the agent of a
 * change that does nothing.
 *
 * @param {WorkingConfig} values - The working config.
 * @param {CalcDetail | undefined} saved - The loaded run.
 * @return {object} Only the settings that differ.
 */
export function evalPendingSettings(
  values: WorkingConfig,
  saved: CalcDetail | undefined
): Record<string, number | string | null> {
  const caseLevel = caseLevelRun(values.evaluationCaseId);
  const now: PendingSettingValues = {
    scenarioId: values.scenarioId,
    evaluationCaseId: values.evaluationCaseId,
    // Calculator-material: eval_calculator reads `effective_date_override` for the
    // scenario/config date and `price_deck_href` for the pricing path.
    effectiveDate: values.effectiveDate,
    priceDeckId: values.priceDeckId,
    ...(caseLevel ? {} : { dcaScenarioId: values.dcaScenarioId }),
  };
  const was: PendingSettingValues = {
    scenarioId: saved?.scenario?.id,
    evaluationCaseId: saved?.configurations?.evaluation_case_id,
    effectiveDate: saved?.configurations?.effective_date,
    // The deck pick the page seeded from this run, not the raw key: a case run's pick lives in
    // its overrides, and an old rerun's price_deck_id can name a deck it never priced on.
    priceDeckId: saved?.configurations
      ? seedPriceDeckId(saved.configurations)
      : undefined,
    ...(caseLevel
      ? {}
      : { dcaScenarioId: saved?.configurations?.dca_scenario_id }),
  };
  return diffPendingSettings(now, was);
}

/**
 * A staged settings change, less what a case-level run would drop silently: its DCA (the run takes
 * the case's). The run is case-level when the page holds a case, or the change selects one.
 * Returns what to tell the user when something was left out, so the assistant's "staged" is never
 * contradicted by a run that ignores it.
 *
 * @param {StagedSettingsChange} change - The parsed staged settings change.
 * @param {number | null} evaluationCaseId - The case the page holds.
 * @return {object} The change to apply (null when nothing is left), and the notice, if any.
 */
export function caseLevelStagedSettings(
  change: StagedSettingsChange,
  evaluationCaseId: number | null
): { change: StagedSettingsChange | null; notice: string | null } {
  const selectsCase = change.operations.some(
    (operation) => operation.field === 'evaluationCaseId'
  );
  if (!caseLevelRun(selectsCase ? -1 : evaluationCaseId)) {
    return { change, notice: null };
  }

  const operations = change.operations.filter(
    (operation) => operation.field !== 'dcaScenarioId'
  );
  if (operations.length === change.operations.length) {
    return { change, notice: null };
  }
  return {
    change: operations.length ? { ...change, operations } : null,
    notice:
      "The DCA scenario wasn't changed: a case's run takes its DCA from the case. Change it on the case page.",
  };
}
